/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {Injectable} from "@angular/core";
import {CommonState} from "@app/store/common/common.state";
import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
// eslint-disable-next-line max-len
import {
  ClinicalDataService,
  ClinicalDataSyncPayload,
  ClinicalDataType,
  TaskDiaryResponse,
  TrecClinicalDataCustom,
  TrecReminder,
  TrecTask,
  TrecTherapy
} from "@trec/api/clinical-data";
import {DrugStoreService, TherapyTemplateGroup} from "@trec/api/drug-store";
import {TrecApiError, TrecError} from "@trec/api/errors";
import {ErrorAction, StoreAction} from "@trec/components/store";
import {addDays, addWeeks, isBefore, subWeeks} from "date-fns";
import {forkJoin, of, throwError} from "rxjs";
import {catchError, delay, switchMap, switchMapTo, tap} from "rxjs/operators";
import {CD_TYPE_THERAPHY_PROFILE_DIABETES} from "../models/cd-type-glycaemia-profile";
import {TpBaseActions} from "./patient-treatment-plan-base.actions";

/**
 * Ngxs treatment plan state.
 */
export interface PatientTreatmentPlanBaseStateModel {
  /**
   * Result of a therapy template group search.
   */
  therapyTemplateGroupsListSearchResults: TherapyTemplateGroup[] | undefined;

  /**
   * Pharma therapies prescribed to patient.
   */
  therapies: TrecTherapy[];
  /**
   * Glycaemia profile.
   */
  glycaemiaProfile: TrecClinicalDataCustom | null;
  /**
   * List of examinations.
   */
  examinations: TrecReminder[];
  /**
   * List of tasks.
   */
  tasks: TrecTask[];
  /**
   * Tasks diary in a custom period of time.
   */
  tasksDiary: TaskDiaryResponse[];

  /**
   * Flag indicating that operation has failed.
   */
  error: TrecError | TrecApiError | null;
}

@State<PatientTreatmentPlanBaseStateModel>({
  name: "tretamentPlanBase",
  defaults: {
    therapyTemplateGroupsListSearchResults: undefined,

    therapies: [],
    glycaemiaProfile: null,
    examinations: [],
    tasks: [],

    tasksDiary: [],

    error: null,
  },
})
@Injectable()
export class PatientTreatmentPlanBaseState {
  constructor(
    private store: Store,
    private drugStore: DrugStoreService,
    private clinicalData: ClinicalDataService
  ) { }

  // Selectors ////////////////////////////////////////////////////////////////////////////////////

  // Available lists ////////////////////////////

  @Selector() static therapyTemplateGroupsListSearchResults(
    state: PatientTreatmentPlanBaseStateModel
  ) {
    return state.therapyTemplateGroupsListSearchResults;
  }

  // Data ///////////////////////////////////////

  @Selector() static therapies(state: PatientTreatmentPlanBaseStateModel) {
    return state.therapies;
  }
  @Selector() static glycaemiaProfile(state: PatientTreatmentPlanBaseStateModel) {
    return state.glycaemiaProfile;
  }
  @Selector() static examinations(state: PatientTreatmentPlanBaseStateModel) {
    return state.examinations;
  }
  @Selector() static tasks(state: PatientTreatmentPlanBaseStateModel) {
    return state.tasks;
  }
  @Selector() static tasksDiary(state: PatientTreatmentPlanBaseStateModel) {
    return state.tasksDiary;
  }
  @Selector() static tasksDiaryMap(state: PatientTreatmentPlanBaseStateModel) {
    return state.tasksDiary.reduce((a, b) => {
      const id = b.task.id || "";
      const arr = a.get(id) || [];
      arr.push(b);
      a.set(id, arr);
      return a;
    }, new Map());
  }

  // Select status //////////////////////////////

  @Selector() static error(state: PatientTreatmentPlanBaseStateModel) {
    return state.error;
  }

  // Actions //////////////////////////////////////////////////////////////////////////////////////

  @Action([
    TpBaseActions.FetchTherapies,
    TpBaseActions.InsertTherapy,
    TpBaseActions.EditTherapy,
    TpBaseActions.InterruptTherapy,
    TpBaseActions.SuspendTherapy,
    TpBaseActions.ResumeTherapy,
    TpBaseActions.FetchGlycaemiaProfile,
    TpBaseActions.UpdateGlycaemiaProfile,
    TpBaseActions.FetchExaminations,
    TpBaseActions.InsertExamination,
    TpBaseActions.EditExamination,
    TpBaseActions.FetchTasks,
    TpBaseActions.InsertTask,
    TpBaseActions.EditTask,
    TpBaseActions.DeleteTask,
    TpBaseActions.DeleteExamination,
  ])
  fetching(ctx: StateContext<PatientTreatmentPlanBaseStateModel>, _action: StoreAction) {
    // Set loading flag to true
    ctx.patchState({error: null});
  }

  @Action([TpBaseActions.TpBaseError, TpBaseActions.SearchTherapyTemplateGroupsError])
  errors(ctx: StateContext<PatientTreatmentPlanBaseStateModel>, {payload}: ErrorAction) {
    // Save error in store
    ctx.patchState({error: payload});
  }

  // Search therapy template groups ///////////////////////////////////////////////////////////////

  @Action(TpBaseActions.SearchTherapyTemplateGroups, {cancelUncompleted: true})
  searchTherapyTemplates(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {query}}: TpBaseActions.SearchTherapyTemplateGroups
  ) {
    return of(null).pipe(
      // Apply a debounce time to avoid storm of search events (-> server has an API limiter)
      delay(300),
      switchMapTo(this.drugStore.searchTherapyTemplateGroup(query)),
      tap((data) => {
        ctx.patchState({therapyTemplateGroupsListSearchResults: data, error: null});
      }),
      catchError((err) => ctx.dispatch(new TpBaseActions.SearchTherapyTemplateGroupsError(err)))
    );
  }

  // Therapies ////////////////////////////////////////////////////////////////////////////////////

  @Action(TpBaseActions.FetchTherapies)
  fetchTherapies(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    { }: TpBaseActions.FetchTherapies
  ) {
    return CommonState.selectOncePatientId(this.store).pipe(
      // Wait a little bit -> why? -> after an operation (create/update/delete) on this type of data
      // FetchTherapies is called immediately to refresh frontend data. However, if fetch call is
      // executed immediately after the operation, server will return old data -> wait to get fresh data
      delay(500),
      switchMap((patientId) => this.clinicalData.getTherapies(patientId)),
      tap((therapies) => {
        ctx.patchState({therapies, error: null});
      }),
      catchError((err) => ctx.dispatch(new TpBaseActions.TpBaseError(err)))
    );
  }

  @Action(TpBaseActions.InsertTherapy)
  insertTherapy(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {therapies}}: TpBaseActions.InsertTherapy
  ) {
    // Add current context as tag
    const context = this.store.selectSnapshot(CommonState.patientContext);
    if (context) {
      therapies.forEach((therapy) => {
        therapy.addTags(context);
      });
    }
    let parentContext = localStorage.getItem("c3-curr-context");

    return forkJoin(therapies.map((therapy) => this.clinicalData.createTherapy(therapy, parentContext))).pipe(
      // On success -> reload therapies list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTherapies())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.EditTherapy)
  updateTherapy(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {therapies}}: TpBaseActions.EditTherapy
  ) {
    return forkJoin(therapies.map((therapy) => this.clinicalData.updateTherapy(therapy))).pipe(
      // On success -> reload therapies list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTherapies())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.InterruptTherapy)
  interruptTherapy(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {therapy, date, structure, doctorId, doctorName}}: TpBaseActions.InterruptTherapy
  ) {
    return this.clinicalData.interruptTherapy(therapy, date, structure, doctorId, doctorName).pipe(
      // On success -> reload therapies list
      switchMap(() => ctx.dispatch(new TpBaseActions.FetchTherapies())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.SuspendTherapy)
  suspendTherapy(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {therapy, date, structure, doctorId, doctorName}}: TpBaseActions.SuspendTherapy
  ) {
    return this.clinicalData.suspendTherapy(therapy, date, structure, doctorId, doctorName).pipe(
      // On success -> reload therapies list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTherapies())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.ResumeTherapy)
  resumeTherapy(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {therapy, date, structure, doctorId, doctorName}}: TpBaseActions.ResumeTherapy
  ) {
    return this.clinicalData.resumeTherapy(therapy, date, structure, doctorId, doctorName, true).pipe(
      // On success -> reload therapies list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTherapies())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  // Glycaemia profile ////////////////////////////////////////////////////////////////////////////

  @Action(TpBaseActions.FetchGlycaemiaProfile)
  fetchGlycaemiaProfile(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    { }: TpBaseActions.FetchGlycaemiaProfile
  ) {
    return CommonState.selectOncePatientId(this.store).pipe(
      switchMap((patientId) =>
        this.clinicalData.getLast<TrecClinicalDataCustom>(CD_TYPE_THERAPHY_PROFILE_DIABETES, {
          patient_id: patientId,
          query: {},
        })
      ),
      tap((glycaemiaProfile) => {
        ctx.patchState({glycaemiaProfile, error: null});
      }),
      catchError((err) => ctx.dispatch(new TpBaseActions.TpBaseError(err)))
    );
  }

  @Action(TpBaseActions.UpdateGlycaemiaProfile)
  updateGlycaemiaProfile(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {glycaemiaProfile}}: TpBaseActions.UpdateGlycaemiaProfile
  ) {
    // Add current context as tag
    const context = this.store.selectSnapshot(CommonState.patientContext);
    if (context) {
      glycaemiaProfile.addTags(context);
    }

    return (
      glycaemiaProfile.id
        ? this.clinicalData.update(glycaemiaProfile)
        : this.clinicalData.insert(glycaemiaProfile)
    ).pipe(
      tap(() => {
        // Operation ended
        ctx.patchState({glycaemiaProfile, error: null});
      }),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  // Examinations /////////////////////////////////////////////////////////////////////////////////

  @Action(TpBaseActions.FetchExaminations)
  fetchExaminations(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    { }: TpBaseActions.FetchExaminations
  ) {
    return CommonState.selectOncePatientId(this.store).pipe(
      // Wait a little bit -> why? -> after an operation (create/update/delete) on this type of data
      // FetchTherapies is called immediately to refresh frontend data. However, if fetch call is
      // executed immediately after the operation, server will return old data -> wait to get fresh data
      delay(500),
      switchMap((patientId) =>
        this.clinicalData.search<TrecReminder>(ClinicalDataType.REMINDER, {
          patient_id: patientId,
          query: {},
        })
      ),
      tap((list) => {
        ctx.patchState({examinations: list, error: null});
      }),
      catchError((err) => ctx.dispatch(new TpBaseActions.TpBaseError(err)))
    );
  }

  @Action(TpBaseActions.InsertExamination)
  insertExamination(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {examinations}}: TpBaseActions.InsertExamination
  ) {
    // Add current context as tag
    const context = this.store.selectSnapshot(CommonState.patientContext);
    if (context) {
      examinations.forEach((exam) => {
        exam.addTags(context);
      });
    }

    console.log('EXAMINATION: ', examinations);

    const payload = new ClinicalDataSyncPayload();
    examinations.forEach((e) => {
      e.data.schedule = computeNextDates(e.metadata.start_date, e.metadata.end_date, e.data.regular.frequency),
        payload.addInsert(e);
    });

    console.log(payload);
    return this.clinicalData.sync(payload).pipe(
      switchMap((results) => {
        console.log('RES: ', results);
        if (results.insert_errors.length !== 0) {
          const err = new TrecError("bad_request");
          return ctx
            .dispatch(new TpBaseActions.TpBaseError(err))
            .pipe(switchMapTo(throwError(err)));
        } else {
          return ctx.dispatch(new TpBaseActions.FetchExaminations()); // On success -> reload list
        }
      }),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.EditExamination)
  editExamination(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {examination}}: TpBaseActions.EditExamination
  ) {
    return this.clinicalData.update(examination).pipe(
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchExaminations())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.DeleteExamination)
  deleteExamination(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {examination}}: TpBaseActions.DeleteExamination
  ) {
    const id = examination.id;
    if (!id) {
      throw new Error("Trying to delete task with unknown id");
    }

    return this.clinicalData.delete(examination.type, id).pipe(
      // On success -> reload list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchExaminations())),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  // Tasks ////////////////////////////////////////////////////////////////////////////////////////

  @Action(TpBaseActions.FetchTasks)
  fetchTasks(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {diaryPeriod}}: TpBaseActions.FetchTasks
  ) {
    // Diary period to fetch (use default if not passed)
    const period = diaryPeriod || {
      start: subWeeks(new Date(), 2).valueOf(),
      end: addWeeks(new Date(), 1).valueOf(),
    };

    return CommonState.selectOncePatientId(this.store).pipe(
      // Wait a little bit -> why? -> after an operation (create/update/delete) on this type of data
      // FetchTherapies is called immediately to refresh frontend data. However, if fetch call is
      // executed immediately after the operation, server will return old data -> wait to get fresh data
      delay(500),
      switchMap((patientId) =>
        forkJoin([
          // Fetch tasks
          this.clinicalData.search<TrecTask>(ClinicalDataType.TASK, {
            patient_id: patientId,
            query: {},
          }),
          // Fetch tasks diary
          this.clinicalData.taskDiaryAll(patientId, period.start, period.end),
        ])
      ),
      tap(([list, diary]) => {
        ctx.patchState({
          tasks: list,
          tasksDiary: Array.from(diary.values()),
          error: null,
        });
      }),
      catchError((err) => ctx.dispatch(new TpBaseActions.TpBaseError(err)))
    );
  }

  @Action(TpBaseActions.InsertTask)
  insertTask(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {task}}: TpBaseActions.InsertTask
  ) {
    // Add current context as tag
    const context = this.store.selectSnapshot(CommonState.patientContext);
    if (context) {
      task.addTags(context);
    }
    let parentContext = localStorage.getItem("c3-curr-context");

    return this.clinicalData.insert(task, undefined, parentContext).pipe(
      // On success -> reload list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTasks({}))),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.EditTask)
  editTask(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {task}}: TpBaseActions.EditTask
  ) {
    const context = this.store.selectSnapshot(CommonState.patientContext);
    if (context) {
      task.addTags(context);
    }
    return this.clinicalData.update(task).pipe(
      // On success -> reload list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTasks({}))),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  @Action(TpBaseActions.DeleteTask)
  deleteTask(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    {payload: {task}}: TpBaseActions.DeleteTask
  ) {
    const id = task.id;
    if (!id) {
      throw new Error("Trying to delete task with unknown id");
    }

    return this.clinicalData.delete(task.type, id).pipe(
      // On success -> reload list
      switchMapTo(ctx.dispatch(new TpBaseActions.FetchTasks({}))),
      catchError((err) =>
        ctx.dispatch(new TpBaseActions.TpBaseError(err)).pipe(switchMapTo(throwError(err)))
      )
    );
  }

  // Clear Status /////////////////////////////////////////////////////////////////////////////////

  @Action(TpBaseActions.ClearTherapyTemplateGroupSearchResultsList)
  clearTherapyTemplateSearchResultsList(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    _action: TpBaseActions.ClearTherapyTemplateGroupSearchResultsList
  ) {
    ctx.patchState({
      therapyTemplateGroupsListSearchResults: [],
    });
  }

  @Action(TpBaseActions.ClearStatus)
  clearStatus(
    ctx: StateContext<PatientTreatmentPlanBaseStateModel>,
    _action: TpBaseActions.ClearStatus
  ) {
    ctx.patchState({
      therapyTemplateGroupsListSearchResults: [],

      therapies: [],
      glycaemiaProfile: null,
      examinations: [],
      tasks: [],

      tasksDiary: [],

      error: null,
    });
  }
}

/**
   * Compute the list of all next occurrences of the exam.
   * @returns A list of dates.
   */
export function computeNextDates(startD: any, endD: any, frequency: any): Array<{scheduled_date: string, execution_date: string, attachment: string, skipped: boolean}> {
  const occurrences: Array<{scheduled_date: string, execution_date: string, attachment: string, skipped: boolean}> = [];

  if (!frequency || !frequency.type) {
    return occurrences;
  }

  // at-need: single (or no) date
  if (frequency.type === 'at_need') {
    console.log('AT NEED DATE: ', startD);
    const d = new Date(startD);
    if (!isNaN(new Date(d).getTime())) {
      occurrences.push({scheduled_date: d.toISOString(), execution_date: '', attachment: '', skipped: true});
    }
    return occurrences;
  }

  // every_x_days: if end date provided, return all occurrences between start and end (inclusive),
  // otherwise return the next upcoming occurrence from start.
  if (frequency.type === 'every_x_days') {
    console.log('EVERY X DAYS DATE: ', startD);
    const interval = (frequency.values && frequency.values[0]) || 0;
    if (!interval || interval <= 0) return occurrences;

    const start = new Date(startD);
    if (isNaN(start.getTime())) return occurrences;

    if (endD) {
      const end = new Date(endD);
      if (isNaN(end.getTime())) return occurrences;
      let cur = start;
      // while cur <= end  -> !(end < cur)
      while (!isBefore(end, cur)) {
        occurrences.push({scheduled_date: formatDateYMD(new Date(cur))!, execution_date: '', attachment: '', skipped: true});
        cur = addDays(cur, interval);
      }
      return occurrences;
    } else {
      // find next occurrence not before today
      let nextDate = start;
      while (isBefore(nextDate, new Date())) {
        nextDate = addDays(nextDate, interval);
      }
      occurrences.push({scheduled_date: formatDateYMD(new Date(nextDate))!, execution_date: '', attachment: '', skipped: true});
      return occurrences;
    }
  }

  return occurrences;
}

export function formatDateYMD(value: any): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  // ISO 8601 → prendo solo la parte di data
  let localeDate = d.toLocaleString().split(',')[0];
  return localeDate.split('/').reverse().join('-');
}